import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetOne, useSidebarState } from "react-admin";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { mapDataChildren, TREE_RESOURCE_NAMES, } from "../ElectionEvents";
import { useTranslation } from "react-i18next";
import MenuActions from "./MenuActions";
import { useActionPermissions } from "../use-tree-menu-hook";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { NewResourceContext } from "@/providers/NewResourceProvider";
import { adminTheme } from "@sequentech/ui-essentials";
import { SettingsContext } from "@/providers/SettingsContextProvider";
import { Box, Menu, MenuItem } from "@mui/material";
import { MenuStyles, TreeMenuItemContainer } from "@/components/styles/Menu";
import { useElectionEventTallyStore } from "@/providers/ElectionEventTallyProvider";
import { useCreateElectionEventStore } from "@/providers/CreateElectionEventContextProvider";
import { useAliasRenderer } from "@/hooks/useAliasRenderer";
export var mapAddResource = {
    sequent_backend_election_event: "createResource.electionEvent",
    sequent_backend_election: "createResource.election",
    sequent_backend_contest: "createResource.contest",
    sequent_backend_candidate: "createResource.candidate",
};
export var mapImportResource = {
    sequent_backend_election_event: "importResource.electionEvent",
    sequent_backend_election: "importResource.election",
    sequent_backend_contest: "importResource.contest",
    sequent_backend_candidate: "importResource.candidate",
};
export function getNavLinkCreate(resource, resourceName) {
    var params = {};
    switch (resourceName) {
        case "sequent_backend_election":
            params.electionEventId = resource.id;
            break;
        case "sequent_backend_contest":
            params.electionEventId = resource.election_event_id;
            params.electionId = resource.id;
            break;
        case "sequent_backend_candidate":
            params.electionEventId = resource.election_event_id;
            params.contestId = resource.id;
            break;
    }
    var url = "/".concat(resourceName, "/create");
    var urlObject = new URL(url, window.location.origin);
    Object.entries(params).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        urlObject.searchParams.append(key, value.toString());
    });
    var res = urlObject.pathname + urlObject.search;
    return res;
}
/**
 * TreeLeaves Component
 *
 * This component renders a tree structure for election events, elections, contests, and candidates.
 * It provides functionality for displaying hierarchical data, managing permissions, and handling
 * actions such as creating or importing election events.
 *
 * @component
 * @param {TreeLeavesProps} props - The props for the TreeLeaves component.
 * @param {Array<DataTreeMenuType>} props.data - The hierarchical data to display in the tree.
 * @param {DataTreeMenuType} props.parentData - The parent data of the current tree level.
 * @param {Array<string>} props.treeResourceNames - The resource names for the tree structure.
 * @param {boolean} props.isArchivedElectionEvents - Indicates if the election events are archived.
 * @param {() => void} props.reloadTree - A callback function to reload the tree structure.
 *
 * @returns {JSX.Element} The rendered TreeLeaves component.
 *
 */
function TreeLeaves(_a) {
    var _b;
    var data = _a.data, parentData = _a.parentData, treeResourceNames = _a.treeResourceNames, isArchivedElectionEvents = _a.isArchivedElectionEvents, reloadTree = _a.reloadTree;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var _d = useCreateElectionEventStore(), openCreateDrawer = _d.openCreateDrawer, openImportDrawer = _d.openImportDrawer;
    var _e = useState(null), anchorEl = _e[0], setAnchorEl = _e[1];
    var aliasRenderer = useAliasRenderer();
    useEffect(function () {
        var dir = i18n.dir(i18n.language);
        document.documentElement.dir = dir;
    }, [i18n, i18n.language, data]);
    /**
     * Permissions
     */
    var _f = useActionPermissions(), canCreateElectionEvent = _f.canCreateElectionEvent, canCreateContest = _f.canCreateContest, canCreateElection = _f.canCreateElection, canCreateCandidate = _f.canCreateCandidate;
    var canShowCreateMenu = (treeResourceNames[0] === "sequent_backend_election_event" && canCreateElectionEvent) ||
        (treeResourceNames[0] === "sequent_backend_election" && canCreateElection) ||
        (treeResourceNames[0] === "sequent_backend_contest" &&
            canCreateContest &&
            canCreateElection) ||
        (treeResourceNames[0] === "sequent_backend_candidate" &&
            canCreateCandidate &&
            canCreateElection &&
            canCreateContest);
    /**
     * ======
     */
    var handleOpenCreateElectionEventMenu = function (e) {
        setAnchorEl(e.currentTarget);
    };
    var handleOpenCreateElectionEventForm = function (e) {
        setAnchorEl(null);
        openCreateDrawer === null || openCreateDrawer === void 0 ? void 0 : openCreateDrawer();
    };
    var handleOpenImportElectionEventForm = function (e) {
        setAnchorEl(null);
        openImportDrawer === null || openImportDrawer === void 0 ? void 0 : openImportDrawer();
    };
    /**
     * @description
     * Given a resource, traverse all its children (elections, contests, candidates)
     * and return an array of all the ids of the children to reopen the tree.
     *
     * @param {DataTreeMenuType} resource - The resource to traverse.
     * @returns {Array<string>} - An array of all the ids of the children.
     */
    var fillPath = useCallback(function (resource) {
        var allIds = [];
        allIds.push(resource.id);
        if ("elections" in resource) {
            for (var _i = 0, _a = resource.elections; _i < _a.length; _i++) {
                var election = _a[_i];
                allIds.push(election.id);
                for (var _b = 0, _c = election.contests; _b < _c.length; _b++) {
                    var contest = _c[_b];
                    allIds.push(contest.id);
                    for (var _d = 0, _e = contest.candidates; _d < _e.length; _d++) {
                        var candidate = _e[_d];
                        allIds.push(candidate.id);
                    }
                }
            }
        }
        else if ("contests" in resource) {
            for (var _f = 0, _g = resource.contests; _f < _g.length; _f++) {
                var contest = _g[_f];
                allIds.push(contest.id);
                for (var _h = 0, _j = contest.candidates; _h < _j.length; _h++) {
                    var candidate = _j[_h];
                    allIds.push(candidate.id);
                }
            }
        }
        else if ("candidates" in resource && resource.candidates !== null) {
            for (var _k = 0, _l = resource.candidates; _k < _l.length; _k++) {
                var candidate = _l[_k];
                allIds.push(candidate.id);
            }
        }
        return allIds;
    }, []);
    return (_jsxs(Box, { sx: { backgroundColor: adminTheme.palette.white }, children: [_jsxs(MenuStyles.TreeLeavesContainer, { children: [(_b = data === null || data === void 0 ? void 0 : data[mapDataChildren(treeResourceNames[0])]) === null || _b === void 0 ? void 0 : _b.map(function (resource) {
                        return (_jsx(TreeMenuItem, { resource: resource, parentData: resource, superParentData: parentData, id: resource.id, name: aliasRenderer(resource), treeResourceNames: treeResourceNames, isArchivedElectionEvents: isArchivedElectionEvents, fullPath: fillPath(resource), reloadTree: reloadTree }, resource.id));
                    }), !isArchivedElectionEvents && canShowCreateMenu && (_jsxs(MenuStyles.CreateElectionContainer, { style: {
                            justifyContent: i18n.dir(i18n.language) === "rtl" ? "end" : "start",
                        }, children: [_jsx(MenuStyles.StyledAddIcon, { style: {
                                    display: i18n.dir(i18n.language) === "rtl" ? "none" : "start",
                                } }), treeResourceNames[0] === TREE_RESOURCE_NAMES[0] ? (_jsx(MenuStyles.StyledNavLinkButton, { className: treeResourceNames[0], style: {
                                    textAlign: i18n.dir(i18n.language) === "rtl" ? "end" : "start",
                                }, onClick: handleOpenCreateElectionEventMenu, children: t(mapAddResource[treeResourceNames[0]]) })) : (_jsx(MenuStyles.StyledNavLink, { className: treeResourceNames[0], to: getNavLinkCreate(parentData, treeResourceNames[0]), style: {
                                    textAlign: i18n.dir(i18n.language) === "rtl" ? "end" : "start",
                                }, children: t(mapAddResource[treeResourceNames[0]]) })), _jsx(MenuStyles.StyledAddIcon, { style: {
                                    display: i18n.dir(i18n.language) === "rtl" ? "block" : "none",
                                } }), _jsx(MenuStyles.StyledHiddenDiv, {})] }))] }), _jsxs(Menu, { id: "treemenu-create-election-event-menu", anchorEl: anchorEl, anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                }, keepMounted: true, transformOrigin: {
                    vertical: "top",
                    horizontal: "right",
                }, open: Boolean(anchorEl), onClose: function () { return setAnchorEl(null); }, children: [_jsx(MenuItem, { className: "menu-sidebar-item", onClick: handleOpenCreateElectionEventForm, children: _jsx(Box, { sx: {
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            }, children: _jsx("span", { className: "help-menu-item", title: "Create Election Event", children: t("createResource.electionEvent") }) }) }), _jsx(MenuItem, { className: "menu-sidebar-item", onClick: handleOpenImportElectionEventForm, children: _jsx(Box, { sx: {
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                            }, children: _jsx("span", { className: "help-menu-item", title: "Import Election Event", children: t("electionEventScreen.import.eetitle") }) }) })] })] }));
}
function TreeMenuItem(_a) {
    var _b, _c, _d;
    var resource = _a.resource, parentData = _a.parentData, superParentData = _a.superParentData, id = _a.id, name = _a.name, treeResourceNames = _a.treeResourceNames, isArchivedElectionEvents = _a.isArchivedElectionEvents, fullPath = _a.fullPath, reloadTree = _a.reloadTree;
    var isOpenSidebar = useSidebarState()[0];
    var i18n = useTranslation().i18n;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var navigate = useNavigate();
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var location = useLocation();
    var _f = useElectionEventTallyStore(), setTallyId = _f.setTallyId, setTaskId = _f.setTaskId, setCustomFilter = _f.setCustomFilter, setCreatedFlag = _f.setCreatedFlag, setCreatingFlag = _f.setCreatingFlag;
    var onClick = function (isLabel) {
        if (isLabel && open) {
            return;
        }
        if (!isLabel && !open) {
            setOpen(true);
            return;
        }
        if (!isLabel && open && resource.active) {
            setOpen(false);
            return;
        }
        if (!isLabel && !open && !resource.active) {
            setOpen(false);
            return;
        }
        if (!open) {
            reloadTree();
        }
        setOpen(false);
    };
    /**
     * control the tree menu open state
     */
    useEffect(function () {
        // set context tally to null to allow navigation to new election event tally
        setTallyId(null);
        setCreatedFlag(false);
        setCreatingFlag(null);
        // set context task to null to allow navigation to new election event task
        setTaskId(null);
        // set context task to null to allow navigation to new election event task
        setCustomFilter({});
        // open menu on url navigation or paste
        setTimeout(function () {
            for (var _i = 0, _a = fullPath !== null && fullPath !== void 0 ? fullPath : []; _i < _a.length; _i++) {
                var id_1 = _a[_i];
                if (id_1 === resource.id) {
                    setOpen(true);
                }
            }
        }, 400);
    }, [location.pathname]);
    var subTreeResourceNames = treeResourceNames.slice(1);
    var nextResourceName = (_b = subTreeResourceNames[0]) !== null && _b !== void 0 ? _b : null;
    var hasNext = !!nextResourceName;
    var key = mapDataChildren(subTreeResourceNames[0]);
    var data = useMemo(function () { return ({}); }, []);
    if (hasNext) {
        data[key] = resource[key];
    }
    var _g = useContext(NewResourceContext), lastCreatedResource = _g.lastCreatedResource, setLastCreatedResource = _g.setLastCreatedResource;
    useEffect(function () {
        if ((lastCreatedResource === null || lastCreatedResource === void 0 ? void 0 : lastCreatedResource.id) === resource.id) {
            setOpen(true);
            setLastCreatedResource(null);
        }
    }, [lastCreatedResource, setLastCreatedResource, resource.id]);
    var menuItemRef = useRef(null);
    var _h = React.useState(null), anchorEl = _h[0], setAnchorEl = _h[1];
    var isClicked = anchorEl ? true : false;
    var tenantId = useTenantStore()[0];
    var imageDocumentId = (_c = resource.image_document_id) !== null && _c !== void 0 ? _c : null;
    var imageData = useGetOne("sequent_backend_document", {
        id: imageDocumentId,
        meta: { tenant_id: tenantId },
    }, {
        enabled: !!imageDocumentId && !!tenantId,
        onError: function (error) {
            console.log("error fetching image doc: ".concat(error.message));
        },
        onSuccess: function () {
            console.log("success fetching image doc");
        },
    }).data;
    var item;
    if (treeResourceNames[0] === "sequent_backend_election_event") {
        item = (_jsxs(MenuStyles.ItemContainer, { children: [_jsx(MenuStyles.HowToVoteStyledIcon, {}), _jsx(MenuStyles.SpanContainer, { children: name })] }));
    }
    else if (imageData) {
        item = (_jsxs(MenuStyles.ItemContainer, { children: [_jsx("img", { alt: name, width: 24, height: 24, src: "".concat(globalSettings.PUBLIC_BUCKET_URL, "tenant-").concat(tenantId, "/document-").concat(imageDocumentId, "/").concat(imageData === null || imageData === void 0 ? void 0 : imageData.name) }), _jsx(MenuStyles.SpanContainer, { children: name })] }));
    }
    else {
        item = (_jsx(MenuStyles.ItemContainer, { children: _jsx(MenuStyles.SpanContainer, { children: name }) }));
    }
    /**
     * Permissions
     */
    var _j = useActionPermissions(), canCreateElectionEvent = _j.canCreateElectionEvent, canReadContest = _j.canReadContest, canReadCandidate = _j.canReadCandidate, canReadElection = _j.canReadElection;
    var canShowMenu = (hasNext && treeResourceNames[0] === "sequent_backend_election_event" && canReadElection) ||
        (hasNext && treeResourceNames[0] === "sequent_backend_election" && canReadContest) ||
        (hasNext && treeResourceNames[0] === "sequent_backend_contest" && canReadCandidate) ||
        (hasNext && treeResourceNames[0] === "sequent_backend_candidate");
    /**
     * ======
     */
    return (_jsxs(Box, { sx: { backgroundColor: adminTheme.palette.white }, children: [_jsxs(TreeMenuItemContainer, { ref: menuItemRef, isClicked: isClicked, children: [canShowMenu ? (_jsx(MenuStyles.TreeMenuIconContaier, { isActive: (_d = resource === null || resource === void 0 ? void 0 : resource.active) !== null && _d !== void 0 ? _d : false, onClick: function () {
                            onClick(false);
                            if (!(resource === null || resource === void 0 ? void 0 : resource.active)) {
                                navigate("/".concat(treeResourceNames[0], "/").concat(id));
                            }
                        }, children: (resource === null || resource === void 0 ? void 0 : resource.active) && open ? (_jsx(ExpandMoreIcon, { className: "menu-item-expanded" })) : (_jsx(ChevronRightIcon, { className: "menu-item-collapsed", style: {
                                transform: i18n.dir(i18n.language) === "rtl"
                                    ? "rotate(180deg)"
                                    : "rotate(0)",
                            } })) })) : (_jsx(MenuStyles.StyledDiv, { isWidth: canCreateElectionEvent })), isOpenSidebar && (_jsx(MenuStyles.StyledSideBarNavLink, { multiline: treeResourceNames[0] === "sequent_backend_election" ? "true" : undefined, onClick: function () { return onClick(true); }, title: name, className: function (_a) {
                            var isActive = _a.isActive;
                            return isActive ? "active menu-item-".concat(treeResourceNames[0]) : "";
                        }, to: "/".concat(treeResourceNames[0], "/").concat(id), style: { textAlign: i18n.dir(i18n.language) === "rtl" ? "end" : "start" }, children: item })), _jsx(MenuStyles.MenuActionContainer, { className: "menu-actions-".concat(treeResourceNames[0]), children: canCreateElectionEvent ? (_jsx(MenuActions, { isArchivedTab: isArchivedElectionEvents, resourceId: id, resourceName: name, resourceType: treeResourceNames[0], parentData: superParentData, menuItemRef: menuItemRef, setAnchorEl: setAnchorEl, anchorEl: anchorEl, reloadTree: reloadTree })) : null })] }), (resource === null || resource === void 0 ? void 0 : resource.active) && open && (_jsx("div", { className: "", children: hasNext && (_jsx(TreeLeaves, { data: data, parentData: parentData, treeResourceNames: subTreeResourceNames, isArchivedElectionEvents: isArchivedElectionEvents, reloadTree: reloadTree })) }))] }));
}
/**
 * TreeMenu component renders a side menu with options to toggle between active and archived election events,
 * and displays a tree structure of election events data.
 *
 * @param {Object} props - The props for the TreeMenu component.
 * @param {DynEntityType} props.data - The data object containing election events and related information.
 * @param {ResourceName[]} props.treeResourceNames - An array of resource names used for the tree structure.
 * @param {boolean} props.isArchivedElectionEvents - A flag indicating whether the archived election events are being viewed.
 * @param {(val: number) => void} props.onArchiveElectionEventsSelect - Callback function triggered when toggling between active and archived election events.
 * @param {() => void} props.reloadTree - Callback function to reload the tree structure.
 *
 * @returns {JSX.Element} The rendered TreeMenu component.
 */
export function TreeMenu(_a) {
    var data = _a.data, treeResourceNames = _a.treeResourceNames, isArchivedElectionEvents = _a.isArchivedElectionEvents, onArchiveElectionEventsSelect = _a.onArchiveElectionEventsSelect, reloadTree = _a.reloadTree;
    var t = useTranslation().t;
    var isEmpty = (!(data === null || data === void 0 ? void 0 : data.electionEvents) || data.electionEvents.length === 0) && isArchivedElectionEvents;
    return (_jsxs(_Fragment, { children: [_jsxs(MenuStyles.SideMenuContainer, { children: [_jsx(MenuStyles.SideMenuActiveItem, { onClick: function () { return onArchiveElectionEventsSelect(0); }, isArchivedElectionEvents: isArchivedElectionEvents, children: t("sideMenu.active") }), _jsx(MenuStyles.SideMenuArchiveItem, { onClick: function () { return onArchiveElectionEventsSelect(1); }, isArchivedElectionEvents: isArchivedElectionEvents, children: t("sideMenu.archived") })] }), _jsx(Box, { sx: { paddingY: 1 }, children: isEmpty ? (_jsx(MenuStyles.EmptyStateContainer, { children: "No Result" })) : (_jsx(TreeLeaves, { data: data, parentData: data, treeResourceNames: treeResourceNames, isArchivedElectionEvents: isArchivedElectionEvents, reloadTree: reloadTree })) })] }));
}
